import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DialogWrapperComponent } from './dialog-wrapper/dialog-wrapper.component';

export type DialogComponent = new (...args: any[]) => {
  data?: any;
  result?: any;
};

@Injectable({ providedIn: 'root' })
export class DialogService {
  private dialog = inject(MatDialog);

  private baseConfig: MatDialogConfig = {
    width: '25rem',
    maxWidth: '100vw',
    height: 'fit-content',
    maxHeight: '98dvh',
    panelClass: 'custom-dialog',
    autoFocus: false,
    scrollStrategy: new NoopScrollStrategy(),
  };

  open<T extends DialogComponent>(
    component: T,
    {
      data,
      afterConfirm,
      afterOpened,
      afterCancel,
      config,
      resizable,
    }: {
      data?: InstanceType<T>['data'];
      afterConfirm?: (result: InstanceType<T>['result']) => void;
      afterOpened?: (instance: InstanceType<T>) => void;
      afterCancel?: () => void;
      config?: MatDialogConfig;
      resizable?: boolean;
    } = {},
  ) {
    data = { originalData: data, originalComponent: component, resizable };
    const dialogRef = this.dialog.open(DialogWrapperComponent<T>, {
      ...this.baseConfig,
      ...this.stackedBackdropConfig(),
      ...config,
      data,
    });
    if (afterConfirm || afterCancel) {
      dialogRef.afterClosed().subscribe((result) => {
        if (result === false || result === undefined || result === null) afterCancel?.();
        else afterConfirm?.(result);
      });
    }
    if (afterOpened) {
      dialogRef.afterOpened().subscribe(() => {
        const { componentInstance } = dialogRef;
        let attempts = 0;
        const checkInstance = () => {
          if (componentInstance.originalInstance) afterOpened(componentInstance.originalInstance);
          else if (attempts++ < 20) setTimeout(() => checkInstance(), 100);
        };
        checkInstance();
      });
    }
    return dialogRef;
  }

  // Every backdrop dims the page again, so stacked dialogs would end up on black.
  private stackedBackdropConfig(): MatDialogConfig {
    return this.dialog.openDialogs.length
      ? { backdropClass: 'cdk-overlay-transparent-backdrop' }
      : {};
  }

  openWide<T extends DialogComponent>(
    component: T,
    params: {
      data?: InstanceType<T>['data'];
      afterConfirm?: (result: InstanceType<T>['result']) => void;
      afterOpened?: (instance: InstanceType<T>) => void;
      afterCancel?: () => void;
      config?: MatDialogConfig;
      resizable?: boolean;
    } = {},
  ) {
    params.resizable ??= true;
    params.config = {
      width: '50rem',
      ...params.config,
    };
    return this.open(component, params);
  }

  openWider<T extends DialogComponent>(
    component: T,
    params: {
      data?: InstanceType<T>['data'];
      afterConfirm?: (result: InstanceType<T>['result']) => void;
      afterOpened?: (instance: InstanceType<T>) => void;
      afterCancel?: () => void;
      config?: MatDialogConfig;
      resizable?: boolean;
    } = {},
  ) {
    params.resizable ??= true;
    params.config = {
      width: '100rem',
      ...params.config,
    };
    return this.open(component, params);
  }

  openWiderTall<T extends DialogComponent>(
    component: T,
    params: {
      data?: InstanceType<T>['data'];
      afterConfirm?: (result: InstanceType<T>['result']) => void;
      afterOpened?: (instance: InstanceType<T>) => void;
      afterCancel?: () => void;
      config?: MatDialogConfig;
      resizable?: boolean;
    } = {},
  ) {
    params.resizable ??= true;
    params.config = {
      width: '100rem',
      height: '98dvh',
      maxHeight: '98dvh',
      ...params.config,
    };
    return this.open(component, params);
  }
}
